import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Crown, ArrowLeft, Save } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { toast } from "sonner";
import { getLifetimeLaunchSettings } from "@/server/lifetime.functions";
import {
  adminUpdateLifetimeSettings,
  adminListLifetimeProviders,
} from "@/server/lifetime-admin.functions";

export const Route = createFileRoute("/dashboard/admin/lifetime")({
  component: AdminLifetimePage,
});

function AdminLifetimePage() {
  const { user, loading, primaryRole } = useAuth();
  const update = useServerFn(adminUpdateLifetimeSettings);
  const list = useServerFn(adminListLifetimeProviders);
  const [s, setS] = useState<any>(null);
  const [providers, setProviders] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [st, pl] = await Promise.all([getLifetimeLaunchSettings(), list()]);
    setS(st);
    setProviders(pl.providers);
  };

  useEffect(() => {
    if (loading || !user || primaryRole !== "admin") return;
    reload();
  }, [loading, user, primaryRole]);

  if (loading || !user || primaryRole !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      await update({
        data: {
          total_lifetime_spots: Number(s.total_lifetime_spots),
          claimed_lifetime_spots: Number(s.claimed_lifetime_spots),
          lifetime_launch_price_cents: Number(s.lifetime_launch_price_cents),
          lifetime_regular_price_cents: Number(s.lifetime_regular_price_cents),
          lifetime_launch_active: !!s.lifetime_launch_active,
        },
      });
      toast.success("Settings saved.");
      reload();
    } catch (e: any) {
      toast.error(e?.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminPageShell
      title="Lifetime Premium Launch"
      description="Manage launch pricing, claim caps, and the list of Lifetime Premium providers."
      primaryAction={{ label: busy ? "Saving…" : "Save settings", icon: Save, onClick: save }}
    >
      <div>


        {s && (
          <section className="mt-6 border border-border bg-card p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Total lifetime spots">
              <Input type="number" value={s.total_lifetime_spots}
                onChange={(e) => setS({ ...s, total_lifetime_spots: e.target.value })} />
            </Field>
            <Field label="Claimed lifetime spots">
              <Input type="number" value={s.claimed_lifetime_spots}
                onChange={(e) => setS({ ...s, claimed_lifetime_spots: e.target.value })} />
            </Field>
            <Field label="Launch price (cents)">
              <Input type="number" value={s.lifetime_launch_price_cents}
                onChange={(e) => setS({ ...s, lifetime_launch_price_cents: e.target.value })} />
            </Field>
            <Field label="Regular price (cents)">
              <Input type="number" value={s.lifetime_regular_price_cents}
                onChange={(e) => setS({ ...s, lifetime_regular_price_cents: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={!!s.lifetime_launch_active}
                onCheckedChange={(v) => setS({ ...s, lifetime_launch_active: !!v })} />
              Launch deal active
            </label>
            <div className="text-sm text-muted-foreground self-center">
              Remaining: <span className="font-semibold text-foreground">
                {Math.max(0, Number(s.total_lifetime_spots) - Number(s.claimed_lifetime_spots))}
              </span>
            </div>
            <div className="sm:col-span-2">
              <Button onClick={save} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
              </Button>
            </div>
          </section>
        )}

        <section className="mt-8">
          <h2 className="text-lg font-semibold mb-3">Lifetime Premium providers ({providers.length})</h2>
          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase text-muted-foreground">
                <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Practice</th><th className="text-left p-3">City</th><th className="text-left p-3">Purchased</th></tr>
              </thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-3">{p.name}</td>
                    <td className="p-3">{p.practice}</td>
                    <td className="p-3">{p.city}{p.state ? `, ${p.state}` : ""}</td>
                    <td className="p-3">{p.lifetime_purchased_at ? new Date(p.lifetime_purchased_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
                {providers.length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No Lifetime Premium providers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </AdminPageShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}
